import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DirtyFlagService } from '@ddv/behaviors';
import { MultiSubscriptionComponent } from '@ddv/common-components';
import { DashboardDetails, DashboardSharingService, EditActionWithEdit, Sharing, ViewTile } from '@ddv/dashboards';
import { Entitlement, UserData, UserEntitlements } from '@ddv/entitlements';
import { QueryParamsService } from '@ddv/filters';
import { ManagerService, Workspace } from '@ddv/layout';
import {
    AppWidgetState,
    Client,
    DashboardFilter,
    DashboardModel,
    DashboardPreference,
    FilterQueryParams,
    Fund,
    FuzzyDates,
    isInvestorQueryType,
    Mode,
    MODE,
    NamedQuery,
    QueryPeriodTypeName,
    toDefaultQueryParams,
    UserPreferences,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { FuzzyDatesService } from '@ddv/reference-data';
import { deepClone, deepCompare } from '@ddv/utils';
import { UserGridColumnOverridesService } from '@ddv/visualizations';
import { DdvWidgetService, WidgetOnBoardService } from '@ddv/widget-on-boards';
import { DropdownOption } from '@hs/ui-core-common';
import { FuzzyDate } from '@hs/ui-core-date';
import { Fund as CcFund, FundAvailability, FundsService as CcFundsService } from '@hs/ui-core-fund';

// this component is NOT a dashboard at all
// it's just the "top bar" of the dashboard that includes the name, the query params, etc
@Component({
    selector: 'app-hs-dashboard',
    templateUrl: './hs-dashboard.component.html',
    styleUrls: ['./hs-dashboard.component.scss'],
    standalone: false,
})
export class HsDashboardComponent extends MultiSubscriptionComponent implements OnChanges, OnInit, OnDestroy {
    @Input() clientCode: string | undefined;
    @Input() isMultiClient = false;
    @Input() user: UserData | undefined;
    @Input() userEntitlements: UserEntitlements | undefined;
    @Input() userPreferences: UserPreferences | undefined;
    @Input() dashboard: DashboardDetails | undefined;
    @Input() dashboardPrefs: DashboardPreference = {};
    @Input() workspace: Workspace | undefined;
    @Input() widgets: AppWidgetState[] | undefined;
    @Input() inPresentationMode = false;
    @Input() memberOf: ViewTile | undefined;
    @Input() useNewExports = false;
    @Input() fuzzyDates: FuzzyDates = new FuzzyDates();
    @Input() ccFuzzyDates: FuzzyDate[] | undefined;
    @Input() clients: Client[] = [];
    @Input() funds: CcFund[] = [];
    @Input() viewMode: string | undefined | null;

    @Output() editRequested = new EventEmitter<string>();
    @Output() cancelEdit = new EventEmitter<void>();
    @Output() revertChanges = new EventEmitter<void>();
    @Output() saveDashboardWidgets = new EventEmitter<DashboardPreference>();
    @Output() dashboardSwitch = new EventEmitter<string>();
    @Output() userPreferencesChange = new EventEmitter<UserPreferences>();

    protected readonly dirtyFlagService = inject(DirtyFlagService);

    private readonly fuzzyDatesService = inject(FuzzyDatesService);
    private readonly manager = inject(ManagerService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly ddvWidgetService = inject(DdvWidgetService);
    private readonly userGridColumnOverridesService = inject(UserGridColumnOverridesService);
    private readonly sharingService = inject(DashboardSharingService);
    private readonly ccFundService = inject(CcFundsService);
    private readonly widgetOnBoardService = inject(WidgetOnBoardService);

    isLastDashboardClosed = false;

    protected queryParams: DashboardPreference | undefined;
    protected showHighlight = true;
    protected showSettings = false;
    protected userSelectedDate: { dateFrom: string, dateTo: string } | undefined;
    protected sharing: Sharing | undefined;
    protected canExport = false;
    protected hasCrosstalkData = false;
    protected namedQueries: NamedQuery[] = [];

    private dashboardParamsChangedOnView = false;
    private detailWidgetId: number | undefined;

    ngOnInit(): void {
        this.subscribeTo(this.queryParamsService.dashboardQueryParams, (dashboardPref: DashboardPreference) => {
            this.queryParams = dashboardPref;
        });

        this.subscribeTo(this.ddvWidgetService.detailWidgetId, (detailWidgetId) => {
            this.detailWidgetId = detailWidgetId;
        });

        // this really should be handled further up
        this.widgetOnBoardService.widgetAddedToBoard
            .subscribe({
                next: () => this.namedQueries = this.getNamedQueriesFromWidgets(this.manager.getWorkspace()?.extraParameters?.widgets),
            });

        // this really should be handled further up
        this.widgetOnBoardService.widgetRemovedFromBoard
            .subscribe({
                next: () => this.namedQueries = this.getNamedQueriesFromWidgets(this.manager.getWorkspace()?.extraParameters?.widgets),
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.userSelectedDate = {
            dateFrom: this.userPreferences?.startDate ?? '',
            dateTo: this.userPreferences?.endDate ?? '' ,
        };

        this.canExport = !!this.userEntitlements?.hasPermission(Entitlement.EXPORT) && this.inPresentationMode;

        if ((changes.clientCode || changes.userPreferences) && this.clientCode) {
            const source = this.userPreferences?.fundNamesSource as FundAvailability.HI | FundAvailability.HSMain | undefined;
            this.ccFundService.loadEntitledFunds(this.clientCode, source)
                .subscribe({
                    next: (funds) => this.funds = funds,
                });
        }

        if (!this.workspace || this.dirtyFlagService.isDirty) {
            return;
        }

        if (changes.workspace || changes.dashboard) {
            this.onCurrentDashboardChanges();
        }

        if (changes.inPresentationMode) {
            this.onModeChanges();
        }
    }

    setHighlightVisibility(visible: boolean): void {
        this.showHighlight = visible;
    }

    protected onUpdateDashboardQueryParams(preference: DashboardPreference): void {
        preference.areFiltersAppliedByMaster = false;
        this.queryParamsService.dispatchUpdatedQueryParams(preference);
        if (!this.inPresentationMode) {
            this.activateDirtyFlagOnParams();
        } else {
            this.dashboardParamsChangedOnView = true;
        }
    }

    protected onApplySelectedClients(clients: Client[]): void {
        if (clientsAreTheSame(clients, this.dashboardPrefs.clients)) {
            return;
        }

        this.queryParamsService.dispatchUpdatedQueryParams({ clients });

        if (!this.inPresentationMode) {
            this.activateDirtyFlagOnParams();
        } else {
            this.dashboardParamsChangedOnView = true;
        }
    }

    protected onClearHighlight(): void {
        this.manager.sendMessageToAllWidgetsOnWorkspace(0, {
            action: 'VISUALIZATION_SELECTED',
            dataSource: [],
            selectedItem: null,
        });
    }

    protected onRefreshCurrentDashboard(): void {
        this.queryParamsService.dispatchNewQueryParams({ ...this.queryParams, isPreferenceChangedOnRefresh: true });
    }

    protected onRestoreCurrentDashboard(): void {
        this.onClearHighlight();
        this.populateDashboardQueryParams();

        if (this.isMultiClient && this.detailWidgetId) {
            this.ddvWidgetService.restoreOriginalViewFilters();
        }

        if (this.workspace) {
            this.subscribeTo(this.userGridColumnOverridesService.deleteOverridesForAllWidgetsOnBoard(this.workspace.id), () => {
                this.manager.sendMessageToAllWidgetsOnWorkspace(0, {
                    action: WIDGET_LIFECYCLE_EVENT.VIEW_RESTORED,
                });

                this.dirtyFlagService.exitDirtyState();
            });
        }
    }

    protected onApplySelectedFunds(ccFunds: CcFund[]): void {
        const funds = this.funds
            .filter((fund) => ccFunds.some((ccFund) => ccFund.code === fund.code))
            .map((fund) => new Fund(fund, this.userPreferences?.fundNamesSource ?? ''));

        this.queryParamsService.dispatchUpdatedQueryParams({
            funds,
            areFiltersAppliedByMaster: false,
        });

        if (!this.inPresentationMode) {
            this.activateDirtyFlagOnParams();
        } else {
            this.dashboardParamsChangedOnView = true;
        }

        this.updateFuzzyDatesForInvestor(funds);
    }

    protected onApplyDateChanges(preference: FilterQueryParams): void {
        this.onUpdateDashboardQueryParams(preference);
    }

    protected onApplyAdditionalFilters(additionalFilters: Record<string, DropdownOption[]>[] | undefined): void {
        this.onUpdateDashboardQueryParams({ additionalFilters });
    }

    protected relayEditAction(action: EditActionWithEdit): void {
        switch (action) {
            case 'edit':
                return this.editRequested.emit(this.workspace?.id ?? '');

            case 'cancel':
                return this.cancelEdit.emit();

            case 'revert':
                return this.revertChanges.emit();

            case 'save':
                return this.saveDashboardWidgets.emit(this.queryParams);
        }
    }

    // this and the two places its called should really move up to view-edit-dashboard
    private getNamedQueriesFromWidgets(widgets: AppWidgetState[] | undefined): NamedQuery[] {
        if (!widgets) {
            return [];
        }

        return Array.from(widgets.reduce((namedQueriesSet, widget) => {
            if (widget.namedQuery) {
                namedQueriesSet.add(widget.namedQuery);
            }

            return namedQueriesSet;
        }, new Set<NamedQuery>()));
    }

    private activateDirtyFlagOnParams(): void {
        if (this.isQueryParamChanged()) {
            this.dirtyFlagService.enterDirtyState(this.workspace?.id ?? '');
        }
    }

    private isQueryParamChanged(): boolean {
        if (!this.manager.getWorkspace()) {
            return false;
        }

        const dashboardModel = this.manager.getWorkspace()?.getExtraParameters();
        let storedLastPref = dashboardModel?.dashboardPreference;

        storedLastPref ??= { funds: [], filters: [], clients: [] };

        return !isViewAndSavedQueryParamsEqual(this.queryParams, storedLastPref);
    }

    private onCurrentDashboardChanges(): void {
        this.populateDashboardQueryParams();

        this.namedQueries = this.getNamedQueriesFromWidgets(this.dashboard?.widgets);

        if (this.workspace && this.clientCode) {
            this.sharingService.getSharing(this.clientCode, this.workspace.id)
                .subscribe({
                    next: (sharing) => this.sharing = sharing,
                });
        } else {
            // this writes back to the input, which is bad and should be fixed later
            this.dashboard = undefined;
            this.sharing = undefined;
        }

        this.hasCrosstalkData = !!this.workspace?.extraParameters?.widgets.some((widget) => {
            return widget.namedQuery?.normalizeConversableType() && widget.currentVisualization === 'ADVANCED_GRID';
        });
    }

    private onModeChanges(): void {
        this.canExport = !!this.userEntitlements?.hasPermission(Entitlement.EXPORT) && this.inPresentationMode;
        const dashboardPreferenceMode = this.inPresentationMode ? MODE.VIEW : MODE.EDIT_WORKSPACE;
        this.updateCachedQueryParamsMode(dashboardPreferenceMode);

        if (!this.inPresentationMode && this.dashboardParamsChangedOnView) {
            this.queryParamsService.dispatchUpdatedQueryParams({ dashboardPreferenceMode });
            this.activateDirtyFlagOnParams();
            this.dashboardParamsChangedOnView = false;
            return;
        }

        const detailWidgetState = this.manager.getWidgetState(this.detailWidgetId ?? 0);
        if (detailWidgetState) {
            this.queryParamsService.dispatchUpdatedQueryParams({ dashboardPreferenceMode });
            return;
        }

        if (this.inPresentationMode) {
            this.syncViewQueryParams();
            if (this.queryParams?.filters?.every((filter) => !filter.isMasterWidgetFilter)) {
                this.manager.updateWidgetsOnMasterFiltersRemoved();
            }
        } else {
            const filters = (this.queryParams?.filters ?? []).filter((filter) => !filter.isMasterWidgetFilter);
            if (filters.length === this.queryParams?.filters?.length) {
                this.queryParamsService.dispatchUpdatedQueryParams({ dashboardPreferenceMode });
                return;
            }

            this.queryParamsService.dispatchUpdatedQueryParams({ dashboardPreferenceMode, filters });
        }
    }

    private syncViewQueryParams(): void {
        const viewQueryParams = this.getNormalizedViewQueryParamsForCurrentWorkspace();

        if (areViewQueryParamsEquivalent(this.queryParams, viewQueryParams)) {
            this.queryParamsService.dispatchUpdatedQueryParams({ dashboardPreferenceMode: MODE.VIEW });
            return;
        }

        this.queryParamsService.dispatchNewQueryParams(viewQueryParams ?? {});
    }

    private getNormalizedViewQueryParamsForCurrentWorkspace(): DashboardPreference | undefined {
        const dashboard = this.manager.getWorkspace();
        const viewQueryParams = dashboard ? this.getDashboardQueryParams(dashboard.getExtraParameters()) : undefined;

        viewQueryParams?.filters?.forEach((filter) => {
            if (filter.values?.some((value) => this.findFuzzyDateByName(value))) {
                this.processFilterValues(filter);
            }
        });

        return viewQueryParams;
    }

    private updateCachedQueryParamsMode(mode: Mode): void {
        this.queryParams = {
            ...this.queryParams,
            dashboardPreferenceMode: mode,
        };
    }

    private populateDashboardQueryParams(): void {
        const dashboard = this.manager.getWorkspace();
        if (dashboard) {
            const extraParams = dashboard.getExtraParameters();
            const params = this.getDashboardQueryParams(extraParams);

            params.filters?.forEach((filter) => {
                if (filter.values?.some((value) => this.findFuzzyDateByName(value))) {
                    this.processFilterValues(filter);
                }
            });

            this.queryParamsService.dispatchNewQueryParams(params);
        }
    }

    private getDashboardQueryParams(extraParams: DashboardModel | undefined): DashboardPreference {
        const preferenceMode = this.inPresentationMode ? MODE.VIEW : MODE.EDIT_WORKSPACE;
        let dashbPref: DashboardPreference = { funds: [], filters: [], dashboardPreferenceMode: preferenceMode, clients: [] };
        if (!this.inPresentationMode && extraParams) {
            const dashboardPref = extraParams.dashboardPreference;
            dashbPref = deepClone(dashboardPref) ?? dashbPref;
            dashbPref.funds = dashbPref.funds ?? [];
            dashbPref.clients = dashbPref.clients ?? [];
            dashbPref.acknowledged = false;
            return dashbPref;
        }

        const workspaceParams = this.manager.getExtraParametersForWorkspace();
        dashbPref = toDefaultQueryParams(
            this.funds.map((fund) => new Fund(fund, this.userPreferences?.fundNamesSource ?? '')),
            this.clients,
            workspaceParams);
        const namedQueries: NamedQuery[] = workspaceParams?.widgets.reduce((queries: NamedQuery[], widget: AppWidgetState) => {
            if (widget.namedQuery) {
                queries.push(widget.namedQuery);
            }
            return queries;
        }, []) ?? [];
        const isCalendarVisible = !namedQueries.length || namedQueries
            .some((nq) => nq.periodType !== (QueryPeriodTypeName.NO_CALENDAR as typeof nq.periodType));
        this.overridePrefWithUserPref(dashbPref);
        this.overridePrefWithSavedParams(dashbPref, extraParams);
        dashbPref.activeDate = dashbPref.activeDate === undefined ? dashbPref.endDate : dashbPref.activeDate;
        dashbPref.dashboardPreferenceMode = preferenceMode;
        if (!isCalendarVisible) {
            dashbPref.startDate = '';
            dashbPref.endDate = '';
        }
        dashbPref.acknowledged = false;
        return dashbPref;
    }

    private overridePrefWithUserPref(pref: DashboardPreference): void {
        if (!this.userPreferences) {
            return;
        }

        if (this.userPreferences.fundsCodes?.length) {
            pref.funds = this.funds
                .filter((f) => this.userPreferences?.fundsCodes.includes(f.code))
                .map((fund) => new Fund(fund, this.userPreferences?.fundNamesSource ?? ''));
        }
        if (this.userPreferences.clientsCodes?.length) {
            pref.clients = this.clients.filter((c) => this.userPreferences?.clientsCodes.includes(c.clientId));
        }
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        pref.startDate = this.userPreferences.startDate || pref.startDate;
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        pref.endDate = this.userPreferences.endDate || pref.endDate;
    }

    private overridePrefWithSavedParams(dashboardPref: DashboardPreference, extraParams: DashboardModel | undefined): void {
        if (extraParams?.dashboardPreference) {
            const savedPref = extraParams.dashboardPreference;
            if (savedPref) {
                if (savedPref.funds?.length) {
                    dashboardPref.funds = savedPref.funds;
                }
                if (savedPref.clients?.length) {
                    dashboardPref.clients = savedPref.clients;
                }
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                dashboardPref.startDate = savedPref.startDate || dashboardPref.startDate;
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                dashboardPref.endDate = savedPref.endDate || dashboardPref.endDate;
                dashboardPref.activeDate = savedPref.activeDate;
                dashboardPref.filters = savedPref.filters;
                dashboardPref.highlight = savedPref.highlight;
                dashboardPref.additionalFilters = savedPref.additionalFilters;
            }
        }
    }

    private updateFuzzyDatesForInvestor(funds: Fund[]): void {
        const widgetsOnBoard = this.workspace?.extraParameters?.widgets ?? [];
        if (widgetsOnBoard.length && this.areAllWidgetsInvestor(widgetsOnBoard)) {
            const updatedFunds = funds.map((fund) => ({ code: fund.fundId }));
            const queriesIds = this.getQueriesIds(widgetsOnBoard);
            this.fuzzyDatesService.pushMostRecentFuzzyDatesForInvestor(updatedFunds, queriesIds).subscribe();
        }
    }

    private findFuzzyDateByName(name: string | number | boolean): FuzzyDate | undefined {
        return this.ccFuzzyDates?.find((fuzzyDate) => fuzzyDate.name === name.toString());
    }

    private processFilterValues(filter: DashboardFilter): void {
        const fuzzies: string[] = [];
        const values: string[] = [];

        filter.values?.forEach((value, index) => {
            const fuzzyDate = this.findFuzzyDateByName(value);
            fuzzies[index] = fuzzyDate?.name ?? '';
            values[index] = fuzzyDate?.commonDate.toDashFormat() ?? value.toString();
        });
        filter.fuzzies = fuzzies;
        filter.values = values;
    }

    private areAllWidgetsInvestor(widgets: AppWidgetState[]): boolean {
        return widgets.every((w) => isInvestorQueryType(w.namedQuery?.normalizeQueryTypeName()));
    }

    private getQueriesIds(widgets: AppWidgetState[]): string[] {
        return widgets.map((w) => w.namedQueryId);
    }
}

function clientsAreTheSame(newClients: Client[], currentClients: Client[] | undefined): boolean {
    if (!currentClients) {
        return false;
    }

    if (newClients.length !== currentClients.length) {
        return false;
    }

    return newClients.every((client) => currentClients.some((c) => c.clientId === client.clientId));
}

function isViewAndSavedQueryParamsEqual(viewPreference?: DashboardPreference, savedPreference?: DashboardPreference): boolean {
    return (!!viewPreference &&
        !!savedPreference &&
        viewPreference.startDate === savedPreference.startDate &&
        viewPreference.endDate === savedPreference.endDate &&
        viewPreference.activeDate === savedPreference.activeDate &&
        deepCompare(viewPreference.filters, savedPreference.filters) &&
        deepCompare(viewPreference.funds, savedPreference.funds)) &&
        deepCompare(viewPreference.clients, savedPreference.clients);
}

function areViewQueryParamsEquivalent(currentPreference?: DashboardPreference, viewPreference?: DashboardPreference): boolean {
    return isViewAndSavedQueryParamsEqual(
        normalizeViewPreferenceForComparison(currentPreference),
        normalizeViewPreferenceForComparison(viewPreference),
    );
}

function normalizeViewPreferenceForComparison(preference: DashboardPreference | undefined): DashboardPreference | undefined {
    if (!preference) {
        return preference;
    }

    return {
        ...preference,
        activeDate: preference.activeDate ?? preference.endDate,
    };
}
